import { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useExpenses } from "../../../lib/expenses/useExpenses";
import type { Expense } from "../../../lib/expenses/types";
import {
  Card,
  EmptyState,
  formatCents,
  withinRange,
  type RangePreset,
} from "./shared";
import { computeRange } from "./shared";

// Palette reused across report charts. Kept inline (not a Tailwind class)
// because recharts expects raw hex.
const PALETTE = [
  "#0070F2",
  "#6E59D9",
  "#E59F4A",
  "#3AAE7C",
  "#D16B82",
  "#22A9B6",
  "#C857B4",
  "#8691A6",
  "#E55A8A",
  "#4C7CE6",
];

export function SpendByCategory({
  preset,
  submittedBy,
}: {
  preset: RangePreset;
  submittedBy?: string | null;
}) {
  const { expenses } = useExpenses();

  const data = useMemo(() => {
    const { startIso, endIso } = computeRange(preset);
    const buckets = new Map<string, number>();
    for (const e of expenses as Expense[]) {
      if (!withinRange(e.occurredOn, startIso, endIso)) continue;
      if (submittedBy && e.submittedBy !== submittedBy) continue;
      const label = e.categoryLabel?.trim() || "Uncategorized";
      buckets.set(label, (buckets.get(label) ?? 0) + e.amountCents);
    }
    return Array.from(buckets.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [expenses, preset, submittedBy]);

  return (
    <Card title="Spend by category" testId="report-spend-by-category">
      {data.length === 0 ? (
        <EmptyState message="No spending in this window yet." />
      ) : (
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                isAnimationActive={false}
                stroke="#fff"
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v: number) => formatCents(v)}
                cursor={false}
              />
              <Legend
                verticalAlign="bottom"
                height={24}
                wrapperStyle={{ fontSize: "12px" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
